import {
  COMPANION_CAPTION_PREFIX,
  DEFAULT_CAPABILITIES,
  DOCUMENTED_CAPTION_PREFIX,
  type InstanceCapabilities,
} from '../instance/capabilities.ts';
import type { InstanceConfig } from '../instance/config.ts';
import { formatUrl, parseUrl, resolveInstanceUrl } from '../instance/urls.ts';

/**
 * R4's `parseVideo(raw)` takes a second argument in practice: R2 puts URL rewriting *in the
 * parser layer* so no component can leak by omission, and rewriting needs to know where the
 * instance is. This is that knowledge, passed explicitly rather than reached for globally (R10).
 */
export interface ParseContext {
  readonly config: InstanceConfig;
  readonly capabilities: InstanceCapabilities;
}

export function createParseContext(
  config: InstanceConfig,
  capabilities: InstanceCapabilities = DEFAULT_CAPABILITIES,
): ParseContext {
  return { config, capabilities };
}

function transportBasePath(config: InstanceConfig): string {
  const base = parseUrl(config.transportBaseUrl);
  if (!base) return '';
  let path = base.path;
  while (path.endsWith('/')) path = path.slice(0, -1);
  return path;
}

/**
 * §2 trap 1 — the `url` the API returns points at the path that answers **200 with a zero-byte
 * body**. Swap the prefix for whichever one the instance probe found to actually return bytes.
 *
 * Returns `null` when the probe found neither (`captionPathPrefix: null`): the caller then has no
 * caption track to offer, which is the honest outcome — better than a track that loads nothing.
 */
export function resolveCaptionUrl(ctx: ParseContext, rawUrl: string): string | null {
  const prefix = ctx.capabilities.captionPathPrefix;
  if (prefix === null) return null;

  const resolved = resolveInstanceUrl(ctx.config, rawUrl);
  if (resolved === null) return null;

  const parts = parseUrl(resolved);
  if (!parts) return null;

  const basePath = transportBasePath(ctx.config);
  const relative = parts.path.startsWith(basePath) ? parts.path.slice(basePath.length) : parts.path;

  let swapped: string;
  if (relative.startsWith(COMPANION_CAPTION_PREFIX)) {
    swapped = prefix + relative.slice(COMPANION_CAPTION_PREFIX.length);
  } else if (relative.startsWith(DOCUMENTED_CAPTION_PREFIX)) {
    swapped = prefix + relative.slice(DOCUMENTED_CAPTION_PREFIX.length);
  } else {
    return resolved;
  }

  return formatUrl({ ...parts, path: `${basePath}${swapped}` });
}
